import {
  SMG_AUTH_WORLDPAY, SMG_ENVIRONMENT, SMG_TRANSACTOR_WORLDPAY, SMG_PAY_PAGE_ID, SMG_REPORT_GROUP, SMG_ePROTECT_URL,
  AMPLITUDE_API_KEY, FINIX_APPLICATION_ID, FINIX_ENVIRONMENT, PAYSAFE_SINGLE_USE_TOKEN_KEY, PAYSAFE_ENVIRONMENT,
  PAYSAFE_ACCOUNT_ID, PAYSAFE_TOKENIZE_TRANSACTION_TYPE, PAYSAFE_TOKENIZE_AMOUNT,
} from 'react-native-dotenv';
import Config from 'react-native-config';
import {Platform} from 'react-native';
import {createClient} from '@connectrpc/connect';
import {SmgService} from '../../generated/smg_service_pb';
import {PartnerService} from '../../generated/partner_service_pb';
import {PublicApiService} from '../../generated/smg_public_api_pb';
import {PlayerBaySessionService} from '../../generated/player_bay_session_pb';
import {smgTransport, partnerTransport, baySessionTransport} from '../grpc/transport';
import {PhoneNumberUtil} from 'google-libphonenumber';
import {colors} from "../assets/styles/colors";
import {resolveSmgEnvironment} from './smgEnvironment';

const resolvedSmgEnvironment = resolveSmgEnvironment(Config.SMG_ENVIRONMENT, SMG_ENVIRONMENT);

export const smgEnvironment = resolvedSmgEnvironment.effective;
// Raw, pre-default environment value: the same inputs as `smgEnvironment` but
// WITHOUT the `|| 'local'` fallback. A bundle whose build pipeline fails to
// bake SMG_ENVIRONMENT leaves this empty/undefined (whereas `smgEnvironment`
// silently becomes the allowlisted 'local'). Security gates that must fail
// closed on an unbaked artifact (see utils/devOverrides) key off this raw
// value; general consumers should keep using `smgEnvironment`.
export const smgEnvironmentRaw = resolvedSmgEnvironment.raw;
export const smgAuthWorldpay = Config.SMG_AUTH_WORLDPAY || SMG_AUTH_WORLDPAY;
export const smgTransactorWorldpay = Config.SMG_TRANSACTOR_WORLDPAY || SMG_TRANSACTOR_WORLDPAY;
export const smgPayPageId = Config.SMG_PAY_PAGE_ID || SMG_PAY_PAGE_ID;
export const smgReportGroup = Config.SMG_REPORT_GROUP || SMG_REPORT_GROUP;
export const smgEProtectUrl = Config.SMG_ePROTECT_URL || SMG_ePROTECT_URL;
// Finix hosted-field tokenization for the withdrawal destination card (SMG-4557). Both must be
// set for Finix card entry to be offered at all: Finix rejects an application id that belongs to
// the other environment, so there is no safe default for either.
export const finixApplicationId = Config.FINIX_APPLICATION_ID || FINIX_APPLICATION_ID;
export const finixEnvironment = Config.FINIX_ENVIRONMENT || FINIX_ENVIRONMENT;
// Paysafe hosted-field tokenization for the withdrawal destination card (SMG-4057). Offered only
// when the server says so (payoff methods `cardEntryProvider: 'PAYSAFE'`) AND the key + environment
// are set here. The key is Paysafe's base64 SINGLE-USE-TOKEN (public) key, which is designed to ship
// in client code; the environment (TEST|LIVE) has no default because the key belongs to one of them.
// The account id is optional (only needed with >1 USD account); transaction type and amount override
// the VERIFICATION / 100 tokenize defaults.
export const paysafeSingleUseTokenKey = Config.PAYSAFE_SINGLE_USE_TOKEN_KEY || PAYSAFE_SINGLE_USE_TOKEN_KEY;
export const paysafeEnvironment = Config.PAYSAFE_ENVIRONMENT || PAYSAFE_ENVIRONMENT;
export const paysafeAccountId = Config.PAYSAFE_ACCOUNT_ID || PAYSAFE_ACCOUNT_ID;
export const paysafeTokenizeTransactionType = Config.PAYSAFE_TOKENIZE_TRANSACTION_TYPE || PAYSAFE_TOKENIZE_TRANSACTION_TYPE;
export const paysafeTokenizeAmount = Config.PAYSAFE_TOKENIZE_AMOUNT || PAYSAFE_TOKENIZE_AMOUNT;
export const amplitudeApiKey = Config.AMPLITUDE_API_KEY || AMPLITUDE_API_KEY || '';

export const SMG_Client = createClient(SmgService, smgTransport);
export const Partner_Client = createClient(PartnerService, partnerTransport);
export const PublicClient = createClient(PublicApiService, smgTransport);
export const PlayerBaySession_Client = createClient(PlayerBaySessionService, baySessionTransport);

// const RomaAccounts = {
//   dev: {deactivated: 6, lastCreated: 38}, internal: {deactivated: 7, lastCreated: 19}, external: {lastCreated: 2}
// };

export const isFullSwing = smgEnvironment.includes('partner');
// export const isFullSwing = false;
// export const isFullSwing = true;

// Mirrors Main.tsx's shouldRedirectToEvenPlayAuth gate: a non-partner-skin
// load carrying `session_id` (native evenplay deep link / manual bay-code
// QR) is EvenPlayAuth traffic, so StartingPage/LANDING_PAGE_URL below route
// there instead of Splash — same way isFullSwing routes PartnerAuth traffic.
const hasEvenPlayAuthSessionId = Platform.OS === 'web' && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('session_id');

export const StartingPage = isFullSwing ? 'PartnerAuth' : hasEvenPlayAuthSessionId ? 'EvenPlayAuth' : 'Splash';

export const Version = 'v1.0.48';

export const ActiveStationIds = smgEnvironment === 'external-partner' ? [6361, 9999, 9007, 9236, 11522, 9036, 1771, 11525, 9197, 4766, 11530, 9074, 9031, 13718] : smgEnvironment === 'partner-prod' ? [13516, 13515, 13514, 9031, 9074, 1771, 11522, 13541, 13718] : [];
export const ActivePromoTypes = ['pga2026'];
export const dateFormat = 'YYYY-MM-DD';
export const ITEM_WEIGHT = 368;
export const ITEM_MARGIN_HORIZONTAL = 5;
export const TOTAL_ITEM_WEIGHT = ITEM_WEIGHT + ITEM_MARGIN_HORIZONTAL * 2;
export const FS_WELCOME_ONBOARDING_PENDING_KEY = 'fs_welcome_onboarding_pending_v1';
export const WELCOME_BONUS_DISPLAY_AMOUNT = '$40';

export const trackStyle = {
  activeBackgroundColor: '#1E9428', inActiveBackgroundColor: 'rgba(187, 188, 191, 1)', borderRadius: 100, width: 51,
  height: 31
};

export const BeforeEachTurnTrackStyle = {
  activeBackgroundColor: '#1E9428', inActiveBackgroundColor: 'rgba(245, 86, 86, 1)', borderRadius: 100, width: 51,
  height: 31
};

export const DisabledBeforeEachTurnTrackStyle = {
  activeBackgroundColor: '#1E9428', inActiveBackgroundColor: 'rgba(187, 188, 191, 1)', borderRadius: 100, width: 51,
  height: 31
};

export const thumbStyle = {
  borderRadius: 28, shadowColor: colors.mainBg, shadowOffset: {width: 0, height: 4}, shadowOpacity: 0.25,
  shadowRadius: 4, elevation: 2, height: 27, width: 27, activeBackgroundColor: 'white', inActiveBackgroundColor: 'white'
};

export const transformImg = (base64: string): string => {
  let val = base64;

  val = val.replace('data:image/png;base64,', '');
  val = val.replace('data:image/jpg;base64,', '');
  val = val.replace('data:image/jpeg;base64,', '');

  return val;
};

export const breakpoint = 1150;
export const tabletBreakpoint = 768;

export const preventSelect: any = Platform.select({web: {userSelect: 'none'}});

export const outline_0: any = Platform.select({web: {outline: 0}});

export const phoneUtil = PhoneNumberUtil.getInstance();

export const FixForTournamentFlatListAlignment = `
<style>
#tournament_list > div > div {
scroll-snap-align: center !important;
}
</style>
`;

export const WebApp = 'https://app.evenplay.com';
export const WebAppText = 'app.evenplay.com';
export const Landing = 'https://evenplay.com';
export const LandingText = 'www.evenplay.com';
export const LANDING_PAGE_URL = smgEnvironment === 'prod' ? Landing : isFullSwing ? '/partner-auth' : hasEvenPlayAuthSessionId ? '/evenplay-auth' : '/splash';
export const SESSION_EXPIRE_TIME = 30; // minutes
export const SupportMail = 'support@evenplay.com';
export const BrandName = 'Evenplay';
